import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { useAuth } from "../../contexts/AuthContext";
import { getDayPost, generateDateID } from "../../services/dayPostService";
import { DayPost, WellnessLog } from "../../types/post";
import { useSubscription } from "../../providers/SubscriptionProvider";

import {
  ChevronLeftIcon,
  CalendarIcon,
  ChevronUpIcon,
  LockClosedIcon,
  ChevronDownIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";

import Header from "../../components/layout/Header";
import WellnessExportDialog from "../../components/dashboard/WellnessExportDialog";
import ComingSoon from "../../components/ComingSoon";
import { useThemeStore } from "../../stores/themeStore";
import AddWellnessLogForm from "../../components/DayPost/AddWellnessLogForm";
import { addWellnessLogWithPeakScore } from "../../services/dayPostService";

interface WellnessData {
  date: string;
  dateLabel: string;
  score: number;
  hasData: boolean;
  health?: number;
  mood?: number;
  physical?: number;
  recovery?: number;
  sleep?: number;
  stress?: number;
  sleepTime?: number;
  soreness?: number;
  injured?: boolean;
  sick?: boolean;
  niggle?: boolean;
  [key: string]: any; // Index signature for Nivo compatibility
}

interface PlotParameter {
  id: string;
  label: string;
  color: string;
  scale?: (value: number) => number; // Function to scale 0-10 to 0-100
}

// Custom hook for responsive screen size detection
const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    isMobile: typeof window !== "undefined" ? window.innerWidth < 768 : false,
    isTablet:
      typeof window !== "undefined"
        ? window.innerWidth >= 768 && window.innerWidth < 1024
        : false,
    isDesktop: typeof window !== "undefined" ? window.innerWidth >= 1024 : true,
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return screenSize;
};

const WellnessInsights: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isMobile, isTablet } = useScreenSize();
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === "peakx-dark";
  const nivoTheme = {
    axis: {
      ticks: {
        text: {
          fill: isDark ? "#e5e5e5" : "#374151",
        },
      },
    },
    grid: {
      line: {
        stroke: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
        strokeWidth: 1,
      },
    },
  };

  // Get subscription status
  const { hasSubscription, isPremium, isPlus } = useSubscription();

  const [wellnessData, setWellnessData] = useState<WellnessData[]>([]);
  const [previousWeekData, setPreviousWeekData] = useState<WellnessData[]>([]);
  const [monthlyData, setMonthlyData] = useState<WellnessData[]>([]);
  const [previousMonthlyData, setPreviousMonthlyData] = useState<
    WellnessData[]
  >([]);
  const [quarterlyData, setQuarterlyData] = useState<WellnessData[]>([]);
  const [previousQuarterlyData, setPreviousQuarterlyData] = useState<
    WellnessData[]
  >([]);
  const [currentDayWellness, setCurrentDayWellness] =
    useState<WellnessLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<
    "Day" | "Week" | "Month" | "Quarter" | "Custom"
  >("Day");
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Plot builder state - only wellness score selected by default for Week view
  const [selectedParameters, setSelectedParameters] = useState<string[]>([
    "score",
  ]);
  const [showPlotBuilder, setShowPlotBuilder] = useState(false);
  const [jitterSeed, setJitterSeed] = useState(0);
  const [showParameterDropdown, setShowParameterDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Wellness export state
  const [wellnessExportOpen, setWellnessExportOpen] = useState(false);

  // Wellness log form state
  const [showWellnessForm, setShowWellnessForm] = useState(false);

  // Custom date range state
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [customRangeData, setCustomRangeData] = useState<WellnessData[]>([]);
  const [customRangeLoading, setCustomRangeLoading] = useState(false);

  // State for Month view display mode (daily vs weekly)
  const [monthViewMode, setMonthViewMode] = useState<"daily" | "weekly">(
    "daily",
  );

  // Premium access - based on subscription status
  // Premium users (premium, platinum, plus, coach) get access to all features
  const hasQuarterlyAccess = isPremium;
  const hasCustomDateAccess = isPremium;
  const hasMonthlyAccess = isPremium || isPlus; // Plus includes 28 Days Analytics
  const hasWeeklyAccess = isPremium || isPlus; // Plus includes 7 & 28 Days Analytics

  // 7-day average comparison toggle
  const [showSevenDayComparison, setShowSevenDayComparison] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowParameterDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Available parameters for plotting (excluding soreness as requested)
  const availableParameters: PlotParameter[] = [
    { id: "score", label: "Wellness Score", color: "#059669" }, // green-600
    {
      id: "health",
      label: "Health",
      color: "#166534", // green-800 (dark green)
      scale: (v) => ((v - 1) / 6) * 100,
    },
    {
      id: "mood",
      label: "Mood",
      color: "#ea580c", // orange-600
      scale: (v) => ((v - 1) / 6) * 100,
    },
    {
      id: "physical",
      label: "Physical",
      color: "#dc2626", // red-600 (dark red)
      scale: (v) => ((v - 1) / 6) * 100,
    },
    {
      id: "recovery",
      label: "Recovery",
      color: "#1e40af", // blue-800 (dark blue)
      scale: (v) => ((v - 1) / 6) * 100,
    },
    {
      id: "sleep",
      label: "Sleep Quality",
      color: "#581c87", // purple-900 (dark purple)
      scale: (v) => ((v - 1) / 6) * 100,
    },
    {
      id: "stress",
      label: "Stress Management",
      color: "#ef4444", // red-500
      scale: (v) => ((v - 1) / 6) * 100,
    },
    {
      id: "sleepTime",
      label: "Sleep Duration",
      color: "#7c3aed", // purple-600
      scale: (v) => ((v - 1) / 6) * 100,
    },
    {
      id: "injured",
      label: "Injured",
      color: "#dc2626", // red-600
    },
    {
      id: "sick",
      label: "Unwell",
      color: "#f59e0b", // amber-500
    },
    {
      id: "niggle",
      label: "Niggle",
      color: "#f97316", // orange-500
    },
  ];

  // Auto-select parameters based on selected tab
  useEffect(() => {
    if (
      selectedTab === "Week" ||
      selectedTab === "Month" ||
      selectedTab === "Quarter" ||
      selectedTab === "Custom"
    ) {
      // For Week, Month, Quarter, and Custom views, set to only wellness score
      setSelectedParameters(["score"]);
    }
  }, [selectedTab]);

  // Helper function to get color based on wellness value (1-7 scale, red to green)
  const getWellnessColor = (value: number): string => {
    const colorScale = [
      "#dc2626", // 1 - red (worst)
      "#f87171",
      "#fca5a5",
      "#fde68a", // 4 - yellow (midpoint)
      "#86efac",
      "#4ade80",
      "#22c55e", // 7 - green (best)
    ];
    return colorScale[value - 1] || "#22c55e";
  };

  // Helper function to get color for soreness (0-10 scale, green to red)
  const getSorenessColor = (value: number): string => {
    const colorScale = [
      "#22c55e", // 0 - green (no soreness)
      "#34d399",
      "#6ee7b7",
      "#a7f3d0",
      "#d9f99d",
      "#fde68a", // 5 - yellowish
      "#fca5a5",
      "#fb7185",
      "#f87171",
      "#ef4444",
      "#dc2626", // 10 - red (high soreness)
    ];
    return colorScale[value] || "#22c55e";
  };

  // Get date from URL parameter, default to today
  const dateParam = searchParams.get("date");
  const targetDate = useMemo(() => {
    if (dateParam && /^\d{8}$/.test(dateParam)) {
      const year = parseInt(dateParam.substring(0, 4));
      const month = parseInt(dateParam.substring(4, 6)) - 1; // Month is 0-indexed
      const day = parseInt(dateParam.substring(6, 8));
      return new Date(year, month, day);
    }
    return new Date();
  }, [dateParam]);

  // Generate past 7 days including the target date
  const generatePastWeekDates = (endDate: Date): Date[] => {
    const dates: Date[] = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      dates.push(date);
    }
    return dates;
  };

  // Generate previous week dates (days 8-14 before target date)
  const generatePreviousWeekDates = (endDate: Date): Date[] => {
    const dates: Date[] = [];
    for (let i = 13; i >= 7; i--) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      dates.push(date);
    }
    return dates;
  };

  // Generate monthly dates (last 28 days)
  const generateMonthlyDates = (endDate: Date): Date[] => {
    const dates: Date[] = [];
    for (let i = 27; i >= 0; i--) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      dates.push(date);
    }
    return dates;
  };

  // Generate previous monthly dates (days 29-56 before target date)
  const generatePreviousMonthlyDates = (endDate: Date): Date[] => {
    const dates: Date[] = [];
    for (let i = 55; i >= 28; i--) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      dates.push(date);
    }
    return dates;
  };

  // Generate quarterly dates (last 84 days / 12 weeks)
  const generateQuarterlyDates = (endDate: Date): Date[] => {
    const dates: Date[] = [];
    for (let i = 83; i >= 0; i--) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      dates.push(date);
    }
    return dates;
  };

  // Generate previous quarterly dates (days 85-168 before target date)
  const generatePreviousQuarterlyDates = (endDate: Date): Date[] => {
    const dates: Date[] = [];
    for (let i = 167; i >= 84; i--) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      dates.push(date);
    }
    return dates;
  };

  // Calculate wellness score from logs
  const calculateWellnessScore = (wellnessLogs: WellnessLog[]): number => {
    if (wellnessLogs.length === 0) return 0;
    const latestLog = wellnessLogs[wellnessLogs.length - 1];
    return latestLog.wellnessScore || 0;
  };

  // Calculate percentage change and format display
  const formatPercentageChange = (currentValue: number, average: number) => {
    if (average === 0) return { text: "N/A", isIncrease: null, percentage: 0 };

    const percentageChange = ((currentValue - average) / average) * 100;
    const isIncrease = percentageChange > 0;
    const absPercentage = Math.abs(percentageChange);

    return {
      text: `${absPercentage.toFixed(0)}%`,
      isIncrease: percentageChange === 0 ? null : isIncrease,
      percentage: percentageChange,
    };
  };

  // Extract all wellness parameters from logs
  const extractWellnessParameters = (wellnessLogs: WellnessLog[]) => {
    if (wellnessLogs.length === 0) return {};
    const latestLog = wellnessLogs[wellnessLogs.length - 1];
    return {
      health: latestLog.health || 0,
      mood: latestLog.mood || 0,
      physical: latestLog.physical || 0,
      recovery: latestLog.recovery || 0,
      sleep: latestLog.sleep || 0,
      stress: latestLog.stress || 0,
      sleepTime: latestLog.sleepTime || 0,
      soreness: latestLog.soreness || 0,
      injured: latestLog.injured ?? false,
      sick: latestLog.sick ?? false,
    };
  };

  // Format date for display
  const formatDateLabel = (date: Date): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }
  };

  // Format date for chart x-axis (day initials)
  const formatDateForChart = (date: Date): string => {
    const dayNames = ["S", "M", "T", "W", "T", "F", "S"];
    return dayNames[date.getDay()];
  };

  // Generate past 28 days
  const generatePast28Days = (endDate: Date): Date[] => {
    const dates: Date[] = [];
    for (let i = 27; i >= 0; i--) {
      // Get 28 days of data
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      dates.push(date);
    }
    return dates;
  };

  // Generate previous 28 days (days 29-56 from target date)
  const generatePrevious28Days = (endDate: Date): Date[] => {
    const dates: Date[] = [];
    for (let i = 55; i >= 28; i--) {
      // Get previous 28 days of data (days 29-56)
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      dates.push(date);
    }
    return dates;
  };

  // Fetch all wellness data when component mounts or date changes
  useEffect(() => {
    const fetchWellnessData = async () => {
      if (!currentUser?.uid) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch current day's detailed wellness data
        const dateID = generateDateID(targetDate);
        const dayPost = await getDayPost(currentUser.uid, dateID);
        if (dayPost && dayPost.wellnessLogs.length > 0) {
          setCurrentDayWellness(
            dayPost.wellnessLogs[dayPost.wellnessLogs.length - 1],
          );
        } else {
          setCurrentDayWellness(null);
        }

        // Fetch week data
        const weekDates = generatePastWeekDates(targetDate);
        const wellnessPromises = weekDates.map(async (date) => {
          const dateID = generateDateID(date);
          try {
            const dayPost = await getDayPost(currentUser.uid, dateID);
            const score = dayPost
              ? calculateWellnessScore(dayPost.wellnessLogs)
              : 0;

            const wellnessParams = extractWellnessParameters(
              dayPost?.wellnessLogs || [],
            );

            return {
              date: dateID,
              dateLabel: formatDateLabel(date),
              score,
              hasData: dayPost !== null && dayPost.wellnessLogs.length > 0,
              ...wellnessParams,
              niggle: (dayPost?.niggleLogs?.length ?? 0) > 0,
            };
          } catch (error) {
            console.error(`Error fetching data for ${dateID}:`, error);
            return {
              date: dateID,
              dateLabel: formatDateLabel(date),
              score: 0,
              hasData: false,
              health: 0,
              mood: 0,
              physical: 0,
              recovery: 0,
              sleep: 0,
              stress: 0,
              sleepTime: 0,
              injured: false,
              sick: false,
              niggle: false,
            };
          }
        });

        const weekResults = await Promise.all(wellnessPromises);
        setWellnessData(weekResults);

        // Fetch previous week data for comparison (days 8-14 before target date)
        const previousWeekDates = generatePreviousWeekDates(targetDate);
        const previousWeekPromises = previousWeekDates.map(async (date) => {
          const dateID = generateDateID(date);
          try {
            const dayPost = await getDayPost(currentUser.uid, dateID);
            const score = dayPost
              ? calculateWellnessScore(dayPost.wellnessLogs)
              : 0;

            const wellnessParams = extractWellnessParameters(
              dayPost?.wellnessLogs || [],
            );

            return {
              date: dateID,
              dateLabel: formatDateLabel(date),
              score,
              hasData: dayPost !== null && dayPost.wellnessLogs.length > 0,
              ...wellnessParams,
              niggle: (dayPost?.niggleLogs?.length ?? 0) > 0,
            };
          } catch (error) {
            console.error(`Error fetching data for ${dateID}:`, error);
            return {
              date: dateID,
              dateLabel: formatDateLabel(date),
              score: 0,
              hasData: false,
              health: 0,
              mood: 0,
              physical: 0,
              recovery: 0,
              sleep: 0,
              stress: 0,
              sleepTime: 0,
              injured: false,
              sick: false,
              niggle: false,
            };
          }
        });

        const previousWeekResults = await Promise.all(previousWeekPromises);
        setPreviousWeekData(previousWeekResults);

        // Fetch monthly data (last 28 days)
        const monthlyDates = generateMonthlyDates(targetDate);
        const monthlyPromises = monthlyDates.map(async (date) => {
          const dateID = generateDateID(date);
          try {
            const dayPost = await getDayPost(currentUser.uid, dateID);
            const score = dayPost
              ? calculateWellnessScore(dayPost.wellnessLogs)
              : 0;

            const wellnessParams = extractWellnessParameters(
              dayPost?.wellnessLogs || [],
            );

            return {
              date: dateID,
              dateLabel: formatDateLabel(date),
              score,
              hasData: dayPost !== null && dayPost.wellnessLogs.length > 0,
              ...wellnessParams,
              niggle: (dayPost?.niggleLogs?.length ?? 0) > 0,
            };
          } catch (error) {
            console.error(`Error fetching data for ${dateID}:`, error);
            return {
              date: dateID,
              dateLabel: formatDateLabel(date),
              score: 0,
              hasData: false,
              health: 0,
              mood: 0,
              physical: 0,
              recovery: 0,
              sleep: 0,
              stress: 0,
              sleepTime: 0,
              injured: false,
              sick: false,
              niggle: false,
            };
          }
        });

        const monthlyResults = await Promise.all(monthlyPromises);
        setMonthlyData(monthlyResults);

        // Fetch previous monthly data (days 29-56)
        const previousMonthlyDates = generatePreviousMonthlyDates(targetDate);
        const previousMonthlyPromises = previousMonthlyDates.map(
          async (date) => {
            const dateID = generateDateID(date);
            try {
              const dayPost = await getDayPost(currentUser.uid, dateID);
              const score = dayPost
                ? calculateWellnessScore(dayPost.wellnessLogs)
                : 0;

              const wellnessParams = extractWellnessParameters(
                dayPost?.wellnessLogs || [],
              );

              return {
                date: dateID,
                dateLabel: formatDateLabel(date),
                score,
                hasData: dayPost !== null && dayPost.wellnessLogs.length > 0,
                ...wellnessParams,
                niggle: (dayPost?.niggleLogs?.length ?? 0) > 0,
              };
            } catch (error) {
              console.error(`Error fetching data for ${dateID}:`, error);
              return {
                date: dateID,
                dateLabel: formatDateLabel(date),
                score: 0,
                hasData: false,
                health: 0,
                mood: 0,
                physical: 0,
                recovery: 0,
                sleep: 0,
                stress: 0,
                sleepTime: 0,
                injured: false,
                sick: false,
                niggle: false,
              };
            }
          },
        );

        const previousMonthlyResults = await Promise.all(
          previousMonthlyPromises,
        );
        setPreviousMonthlyData(previousMonthlyResults);

        // Fetch quarterly data (last 84 days / 12 weeks)
        const quarterlyDates = generateQuarterlyDates(targetDate);
        const quarterlyPromises = quarterlyDates.map(async (date) => {
          const dateID = generateDateID(date);
          try {
            const dayPost = await getDayPost(currentUser.uid, dateID);
            const score = dayPost
              ? calculateWellnessScore(dayPost.wellnessLogs)
              : 0;

            const wellnessParams = extractWellnessParameters(
              dayPost?.wellnessLogs || [],
            );

            return {
              date: dateID,
              dateLabel: formatDateLabel(date),
              score,
              hasData: dayPost !== null && dayPost.wellnessLogs.length > 0,
              ...wellnessParams,
              niggle: (dayPost?.niggleLogs?.length ?? 0) > 0,
            };
          } catch (error) {
            console.error(`Error fetching data for ${dateID}:`, error);
            return {
              date: dateID,
              dateLabel: formatDateLabel(date),
              score: 0,
              hasData: false,
              health: 0,
              mood: 0,
              physical: 0,
              recovery: 0,
              sleep: 0,
              stress: 0,
              sleepTime: 0,
              injured: false,
              sick: false,
              niggle: false,
            };
          }
        });

        const quarterlyResults = await Promise.all(quarterlyPromises);
        setQuarterlyData(quarterlyResults);

        // Fetch previous quarterly data (days 85-168)
        const previousQuarterlyDates =
          generatePreviousQuarterlyDates(targetDate);
        const previousQuarterlyPromises = previousQuarterlyDates.map(
          async (date) => {
            const dateID = generateDateID(date);
            try {
              const dayPost = await getDayPost(currentUser.uid, dateID);
              const score = dayPost
                ? calculateWellnessScore(dayPost.wellnessLogs)
                : 0;

              const wellnessParams = extractWellnessParameters(
                dayPost?.wellnessLogs || [],
              );

              return {
                date: dateID,
                dateLabel: formatDateLabel(date),
                score,
                hasData: dayPost !== null && dayPost.wellnessLogs.length > 0,
                ...wellnessParams,
                niggle: (dayPost?.niggleLogs?.length ?? 0) > 0,
              };
            } catch (error) {
              console.error(`Error fetching data for ${dateID}:`, error);
              return {
                date: dateID,
                dateLabel: formatDateLabel(date),
                score: 0,
                hasData: false,
                health: 0,
                mood: 0,
                physical: 0,
                recovery: 0,
                sleep: 0,
                stress: 0,
                sleepTime: 0,
                injured: false,
                sick: false,
                niggle: false,
              };
            }
          },
        );

        const previousQuarterlyResults = await Promise.all(
          previousQuarterlyPromises,
        );
        setPreviousQuarterlyData(previousQuarterlyResults);
      } catch (err) {
        console.error("Error fetching wellness data:", err);
        setError("Failed to load wellness data");
      } finally {
        setLoading(false);
      }
    };

    fetchWellnessData();
  }, [currentUser?.uid, targetDate]);

  // Fetch custom date range data
  const fetchCustomRangeData = async (startDate: string, endDate: string) => {
    if (!startDate || !endDate || !currentUser?.uid) {
      return;
    }

    setCustomRangeLoading(true);
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Calculate the number of days in the range
      const daysDiff =
        Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) +
        1;

      // If more than 28 days, use weekly averages
      if (daysDiff > 28) {
        // Generate weeks for the custom range
        const weeks: Date[][] = [];
        let currentWeekStart = new Date(start);

        while (currentWeekStart <= end) {
          const weekDates: Date[] = [];
          for (let i = 0; i < 7; i++) {
            const date = new Date(currentWeekStart);
            date.setDate(currentWeekStart.getDate() + i);
            if (date <= end) {
              weekDates.push(date);
            }
          }
          if (weekDates.length > 0) {
            weeks.push(weekDates);
          }
          currentWeekStart.setDate(currentWeekStart.getDate() + 7);
        }

        // Fetch weekly averages
        const weekPromises = weeks.map(async (weekDates) => {
          const weekStartDate = weekDates[0];
          const weekLabel = weekStartDate.toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
          });

          const weekDataPromises = weekDates.map(async (date) => {
            const dateID = generateDateID(date);
            try {
              const dayPost = await getDayPost(currentUser.uid, dateID);
              return dayPost;
            } catch (error) {
              return null;
            }
          });

          const weekData = await Promise.all(weekDataPromises);
          const validDays = weekData.filter(
            (day): day is NonNullable<typeof day> =>
              day !== null &&
              day !== undefined &&
              day.wellnessLogs &&
              day.wellnessLogs.length > 0,
          );

          if (validDays.length === 0) {
            return {
              date: weekLabel,
              dateLabel: weekLabel,
              score: 0,
              hasData: false,
              health: 0,
              mood: 0,
              physical: 0,
              recovery: 0,
              sleep: 0,
              stress: 0,
              sleepTime: 0,
              soreness: 0,
              injured: false,
              sick: false,
              niggle: false,
            };
          }

          // Calculate weekly averages
          const avgScore =
            validDays.reduce(
              (sum, day) => sum + calculateWellnessScore(day.wellnessLogs),
              0,
            ) / validDays.length;

          // Extract all parameters and calculate averages
          const allParams = validDays.map((day) =>
            extractWellnessParameters(day.wellnessLogs),
          );

          const avgHealth =
            allParams.reduce((sum, params) => sum + (params.health || 0), 0) /
            validDays.length;
          const avgMood =
            allParams.reduce((sum, params) => sum + (params.mood || 0), 0) /
            validDays.length;
          const avgPhysical =
            allParams.reduce((sum, params) => sum + (params.physical || 0), 0) /
            validDays.length;
          const avgRecovery =
            allParams.reduce((sum, params) => sum + (params.recovery || 0), 0) /
            validDays.length;
          const avgSleep =
            allParams.reduce((sum, params) => sum + (params.sleep || 0), 0) /
            validDays.length;
          const avgStress =
            allParams.reduce((sum, params) => sum + (params.stress || 0), 0) /
            validDays.length;
          const avgSleepTime =
            allParams.reduce(
              (sum, params) => sum + (params.sleepTime || 0),
              0,
            ) / validDays.length;
          const avgSoreness =
            allParams.reduce((sum, params) => sum + (params.soreness || 0), 0) /
            validDays.length;

          return {
            date: weekLabel,
            dateLabel: weekLabel,
            score: avgScore,
            hasData: true,
            health: avgHealth,
            mood: avgMood,
            physical: avgPhysical,
            recovery: avgRecovery,
            sleep: avgSleep,
            stress: avgStress,
            sleepTime: avgSleepTime,
            soreness: avgSoreness,
            injured: validDays.some((day: any) => day.injuredLogs?.length > 0),
            sick: validDays.some(
              (day: any) =>
                day.sickLogs?.length > 0 || day.illnessLogs?.length > 0,
            ),
            niggle: validDays.some((day: any) => day.niggleLogs?.length > 0),
          };
        });

        const results = await Promise.all(weekPromises);
        setCustomRangeData(results);
      } else {
        // Use daily data for 28 days or less
        const dates: Date[] = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          dates.push(new Date(d));
        }

        const promises = dates.map(async (date) => {
          const dateID = generateDateID(date);
          const dateLabel = date.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
          });

          try {
            const dayPost = await getDayPost(currentUser.uid, dateID);

            if (
              !dayPost ||
              !dayPost.wellnessLogs ||
              dayPost.wellnessLogs.length === 0
            ) {
              return {
                date: dateID,
                dateLabel,
                score: 0,
                hasData: false,
                health: 0,
                mood: 0,
                physical: 0,
                recovery: 0,
                sleep: 0,
                stress: 0,
                sleepTime: 0,
                soreness: 0,
                injured: false,
                sick: false,
                niggle: false,
              };
            }

            const score = calculateWellnessScore(dayPost.wellnessLogs);
            const wellnessParams = extractWellnessParameters(
              dayPost.wellnessLogs,
            );

            return {
              date: dateID,
              dateLabel,
              score,
              hasData: true,
              ...wellnessParams,
              niggle: (dayPost?.niggleLogs?.length ?? 0) > 0,
            };
          } catch (error) {
            return {
              date: dateID,
              dateLabel,
              score: 0,
              hasData: false,
              health: 0,
              mood: 0,
              physical: 0,
              recovery: 0,
              sleep: 0,
              stress: 0,
              sleepTime: 0,
              soreness: 0,
              injured: false,
              sick: false,
              niggle: false,
            };
          }
        });

        const results = await Promise.all(promises);
        setCustomRangeData(results);
      }
    } catch (error) {
      console.error("Error fetching custom range data:", error);
    } finally {
      setCustomRangeLoading(false);
    }
  };

  // Transform data for line chart (excluding wellness score which will be bars)
  const transformDataForLineChart = useMemo(() => {
    if (
      (selectedTab !== "Week" && selectedTab !== "Custom") ||
      selectedParameters.length === 0
    )
      return [];

    const lineChartParams = selectedParameters.filter(
      (paramId) =>
        paramId !== "score" &&
        paramId !== "injured" &&
        paramId !== "sick" &&
        paramId !== "niggle",
    );

    // Determine which data source to use
    const dataSource =
      selectedTab === "Custom" ? customRangeData : wellnessData;

    // If no line chart params, create dummy data to establish x-axis scale (for score bars or injury flags)
    if (lineChartParams.length === 0) {
      if (selectedTab === "Custom") {
        return [
          {
            id: "Wellness Score",
            color: "transparent",
            data: customRangeData.map((d) => ({
              x: d.dateLabel,
              y: 0,
            })),
          },
        ];
      } else {
        const weekDates = generatePastWeekDates(targetDate);
        return [
          {
            id: "Wellness Score",
            color: "transparent",
            data: weekDates.map((date) => ({
              x: formatDateLabel(date),
              y: 0,
            })),
          },
        ];
      }
    }

    return lineChartParams
      .map((paramId) => {
        const param = availableParameters.find((p) => p.id === paramId);
        if (!param) return null;

        let data;

        if (selectedTab === "Custom") {
          // Use custom range data
          data = customRangeData
            .map((dayData) => {
              // Only include data points if we have actual data and the original value is not 0
              if (
                dayData &&
                dayData.hasData &&
                dayData[paramId] !== undefined
              ) {
                const originalValue = dayData[paramId] || 0;

                // Skip if original value is 0 (before scaling)
                if (originalValue === 0) {
                  return null;
                }

                let scaledValue = originalValue;
                // Scale 1-7 values to 0-100 if scaling function exists
                if (param.scale && paramId !== "score") {
                  scaledValue = param.scale(originalValue);
                }

                return {
                  x: dayData.dateLabel,
                  y: scaledValue,
                };
              }
              return null;
            })
            .filter((d) => d !== null);
        } else {
          // Use week data
          const weekDates = generatePastWeekDates(targetDate);

          data = weekDates
            .map((date) => {
              const dateID = generateDateID(date);
              const dayData = wellnessData.find((d) => d.date === dateID);

              // Only include data points if we have actual data and the original value is not 0
              if (
                dayData &&
                dayData.hasData &&
                dayData[paramId] !== undefined
              ) {
                const originalValue = dayData[paramId] || 0;

                // Skip if original value is 0 (before scaling)
                if (originalValue === 0) {
                  return null;
                }

                let scaledValue = originalValue;
                // Scale 1-7 values to 0-100 if scaling function exists
                if (param.scale && paramId !== "score") {
                  scaledValue = param.scale(originalValue);
                }

                return {
                  x: formatDateLabel(date),
                  y: scaledValue,
                };
              }

              return null;
            })
            .filter(
              (point): point is { x: string; y: number } => point !== null,
            );
        }

        return {
          id: param.label,
          color: param.color,
          data: data,
        };
      })
      .filter(
        (
          item,
        ): item is {
          id: string;
          color: string;
          data: { x: string; y: number }[];
        } => item !== null && item.data.length > 0,
      );
  }, [
    selectedTab,
    selectedParameters,
    wellnessData,
    customRangeData,
    availableParameters,
    targetDate,
  ]);

  // Transform data for wellness score bars (only when score is selected)
  const transformDataForBars = useMemo(() => {
    if (
      (selectedTab !== "Week" && selectedTab !== "Custom") ||
      !selectedParameters.includes("score")
    )
      return [];

    if (selectedTab === "Custom") {
      return customRangeData.map((dayData) => ({
        day: dayData.dateLabel,
        score: dayData && dayData.hasData ? dayData.score || 0 : 0,
        hasData: dayData ? dayData.hasData : false,
        injured: dayData?.injured ?? false,
        sick: dayData?.sick ?? false,
      }));
    }

    const weekDates = generatePastWeekDates(targetDate);

    return weekDates.map((date) => {
      const dateID = generateDateID(date);
      const dayData = wellnessData.find((d) => d.date === dateID);

      return {
        day: formatDateLabel(date),
        score: dayData && dayData.hasData ? dayData.score || 0 : 0,
        hasData: dayData ? dayData.hasData : false,
        injured: dayData?.injured ?? false,
        sick: dayData?.sick ?? false,
      };
    });
  }, [
    selectedTab,
    wellnessData,
    customRangeData,
    selectedParameters,
    targetDate,
  ]);

  // Transform monthly data to weekly averages (4 weeks)
  const transformMonthlyDataForLineChart = useMemo(() => {
    if (selectedTab !== "Month" || selectedParameters.length === 0) return [];

    const lineChartParams = selectedParameters.filter(
      (paramId) =>
        paramId !== "score" &&
        paramId !== "injured" &&
        paramId !== "sick" &&
        paramId !== "niggle",
    );

    if (lineChartParams.length === 0) {
      return [
        {
          id: "Wellness Score",
          color: "transparent",
          data: ["Week 1", "Week 2", "Week 3", "Week 4"].map((week) => ({
            x: week,
            y: 0,
          })),
        },
      ];
    }

    return lineChartParams
      .map((paramId) => {
        const param = availableParameters.find((p) => p.id === paramId);
        if (!param) return null;

        // Calculate weekly averages (4 weeks from 28 days)
        const weeklyData = [];
        for (let week = 0; week < 4; week++) {
          const weekStart = week * 7;
          const weekEnd = weekStart + 7;
          const weekData = monthlyData.slice(weekStart, weekEnd);

          const validData = weekData.filter(
            (d) => d.hasData && d[paramId] !== undefined && d[paramId] > 0,
          );

          if (validData.length > 0) {
            const avg =
              validData.reduce((sum, d) => sum + (d[paramId] || 0), 0) /
              validData.length;

            let scaledValue = avg;
            if (param.scale && paramId !== "score") {
              scaledValue = param.scale(avg);
            }

            weeklyData.push({
              x: `Week ${week + 1}`,
              y: scaledValue,
            });
          }
        }

        return {
          id: param.label,
          color: param.color,
          data: weeklyData,
        };
      })
      .filter(
        (
          item,
        ): item is {
          id: string;
          color: string;
          data: { x: string; y: number }[];
        } => item !== null && item.data.length > 0,
      );
  }, [selectedTab, selectedParameters, monthlyData, availableParameters]);

  // Transform monthly data for wellness score bars
  const transformMonthlyDataForBars = useMemo(() => {
    if (selectedTab !== "Month" || !selectedParameters.includes("score"))
      return [];

    const weeklyScores = [];
    for (let week = 0; week < 4; week++) {
      const weekStart = week * 7;
      const weekEnd = weekStart + 7;
      const weekData = monthlyData.slice(weekStart, weekEnd);

      const validData = weekData.filter((d) => d.hasData);
      const avgScore =
        validData.length > 0
          ? validData.reduce((sum, d) => sum + d.score, 0) / validData.length
          : 0;

      weeklyScores.push({
        day: `Week ${week + 1}`,
        score: avgScore,
        hasData: validData.length > 0,
        injured: weekData.some((d) => d.injured),
        sick: weekData.some((d) => d.sick),
      });
    }

    return weeklyScores;
  }, [selectedTab, monthlyData, selectedParameters]);

  // Transform monthly data for daily line chart (28 individual days)
  const transformMonthlyDailyDataForLineChart = useMemo(() => {
    if (selectedTab !== "Month" || selectedParameters.length === 0) return [];

    const lineChartParams = selectedParameters.filter(
      (paramId) =>
        paramId !== "score" &&
        paramId !== "injured" &&
        paramId !== "sick" &&
        paramId !== "niggle",
    );

    if (lineChartParams.length === 0) {
      return [
        {
          id: "Wellness Score",
          color: "transparent",
          data: monthlyData.map((d) => ({
            x: d.dateLabel,
            y: 0,
          })),
        },
      ];
    }

    return lineChartParams
      .map((paramId) => {
        const param = availableParameters.find((p) => p.id === paramId);
        if (!param) return null;

        const data = monthlyData
          .map((dayData) => {
            if (
              dayData &&
              dayData.hasData &&
              dayData[paramId] !== undefined &&
              dayData[paramId] > 0
            ) {
              let scaledValue = dayData[paramId] || 0;
              if (param.scale && paramId !== "score") {
                scaledValue = param.scale(dayData[paramId]);
              }
              return { x: dayData.dateLabel, y: scaledValue };
            }
            return null;
          })
          .filter((d): d is { x: string; y: number } => d !== null);

        return {
          id: param.label,
          color: param.color,
          data,
        };
      })
      .filter(
        (
          item,
        ): item is {
          id: string;
          color: string;
          data: { x: string; y: number }[];
        } => item !== null && item.data.length > 0,
      );
  }, [selectedTab, selectedParameters, monthlyData, availableParameters]);

  // Transform monthly data for daily wellness score bars (28 individual days)
  const transformMonthlyDailyDataForBars = useMemo(() => {
    if (selectedTab !== "Month" || !selectedParameters.includes("score"))
      return [];

    return monthlyData.map((dayData) => ({
      day: dayData.dateLabel,
      score: dayData && dayData.hasData ? dayData.score || 0 : 0,
      hasData: dayData ? dayData.hasData : false,
      injured: dayData?.injured ?? false,
      sick: dayData?.sick ?? false,
    }));
  }, [selectedTab, monthlyData, selectedParameters]);

  // Transform quarterly data to weekly averages (12 weeks)
  const transformQuarterlyDataForLineChart = useMemo(() => {
    if (selectedTab !== "Quarter" || selectedParameters.length === 0) return [];

    const lineChartParams = selectedParameters.filter(
      (paramId) =>
        paramId !== "score" &&
        paramId !== "injured" &&
        paramId !== "sick" &&
        paramId !== "niggle",
    );

    if (lineChartParams.length === 0) {
      return [
        {
          id: "Wellness Score",
          color: "transparent",
          data: Array.from({ length: 12 }, (_, i) => ({
            x: `W${i + 1}`,
            y: 0,
          })),
        },
      ];
    }

    return lineChartParams
      .map((paramId) => {
        const param = availableParameters.find((p) => p.id === paramId);
        if (!param) return null;

        // Calculate weekly averages (12 weeks from 84 days)
        const weeklyData = [];
        for (let week = 0; week < 12; week++) {
          const weekStart = week * 7;
          const weekEnd = weekStart + 7;
          const weekData = quarterlyData.slice(weekStart, weekEnd);

          const validData = weekData.filter(
            (d) => d.hasData && d[paramId] !== undefined && d[paramId] > 0,
          );

          if (validData.length > 0) {
            const avg =
              validData.reduce((sum, d) => sum + (d[paramId] || 0), 0) /
              validData.length;

            let scaledValue = avg;
            if (param.scale && paramId !== "score") {
              scaledValue = param.scale(avg);
            }

            weeklyData.push({
              x: `W${week + 1}`,
              y: scaledValue,
            });
          }
        }

        return {
          id: param.label,
          color: param.color,
          data: weeklyData,
        };
      })
      .filter(
        (
          item,
        ): item is {
          id: string;
          color: string;
          data: { x: string; y: number }[];
        } => item !== null && item.data.length > 0,
      );
  }, [selectedTab, selectedParameters, quarterlyData, availableParameters]);

  // Transform quarterly data for wellness score bars
  const transformQuarterlyDataForBars = useMemo(() => {
    if (selectedTab !== "Quarter" || !selectedParameters.includes("score"))
      return [];

    const weeklyScores = [];
    for (let week = 0; week < 12; week++) {
      const weekStart = week * 7;
      const weekEnd = weekStart + 7;
      const weekData = quarterlyData.slice(weekStart, weekEnd);

      const validData = weekData.filter((d) => d.hasData);
      const avgScore =
        validData.length > 0
          ? validData.reduce((sum, d) => sum + d.score, 0) / validData.length
          : 0;

      weeklyScores.push({
        day: `W${week + 1}`,
        score: avgScore,
        hasData: validData.length > 0,
        injured: weekData.some((d) => d.injured),
        sick: weekData.some((d) => d.sick),
      });
    }

    return weeklyScores;
  }, [selectedTab, quarterlyData, selectedParameters]);

  // Flag data for injury/sick markers - independent of "score" selection
  const flagData = useMemo(() => {
    const showInjured = selectedParameters.includes("injured");
    const showSick = selectedParameters.includes("sick");
    const showNiggle = selectedParameters.includes("niggle");
    if (!showInjured && !showSick && !showNiggle) return [];

    if (selectedTab === "Week") {
      const weekDates = generatePastWeekDates(targetDate);
      return weekDates.map((date) => {
        const dateID = generateDateID(date);
        const dayData = wellnessData.find((d) => d.date === dateID);
        return {
          day: formatDateLabel(date),
          hasData: dayData ? dayData.hasData : false,
          injured: dayData?.injured ?? false,
          sick: dayData?.sick ?? false,
          niggle: dayData?.niggle ?? false,
        };
      });
    }

    if (selectedTab === "Month") {
      if (monthViewMode === "daily") {
        return monthlyData.map((dayData) => ({
          day: dayData.dateLabel,
          hasData: dayData ? dayData.hasData : false,
          injured: dayData?.injured ?? false,
          sick: dayData?.sick ?? false,
          niggle: dayData?.niggle ?? false,
        }));
      }
      // Weekly aggregation
      const weeklyFlags = [];
      for (let week = 0; week < 4; week++) {
        const weekStart = week * 7;
        const weekEnd = weekStart + 7;
        const weekData = monthlyData.slice(weekStart, weekEnd);
        weeklyFlags.push({
          day: `Week ${week + 1}`,
          hasData: weekData.some((d) => d.hasData),
          injured: weekData.some((d) => d.injured),
          sick: weekData.some((d) => d.sick),
          niggle: weekData.some((d) => d.niggle),
        });
      }
      return weeklyFlags;
    }

    if (selectedTab === "Quarter") {
      const weeklyFlags = [];
      for (let week = 0; week < 12; week++) {
        const weekStart = week * 7;
        const weekEnd = weekStart + 7;
        const weekData = quarterlyData.slice(weekStart, weekEnd);
        weeklyFlags.push({
          day: `W${week + 1}`,
          hasData: weekData.some((d) => d.hasData),
          injured: weekData.some((d) => d.injured),
          sick: weekData.some((d) => d.sick),
          niggle: weekData.some((d) => d.niggle),
        });
      }
      return weeklyFlags;
    }

    if (selectedTab === "Custom") {
      return customRangeData.map((dayData) => ({
        day: dayData.dateLabel,
        hasData: dayData ? dayData.hasData : false,
        injured: dayData?.injured ?? false,
        sick: dayData?.sick ?? false,
        niggle: dayData?.niggle ?? false,
      }));
    }

    return [];
  }, [
    selectedTab,
    selectedParameters,
    wellnessData,
    monthlyData,
    quarterlyData,
    customRangeData,
    monthViewMode,
    targetDate,
  ]);

  // Calculate wellness category insights for Week view
  const weeklyWellnessInsights = useMemo(() => {
    if (selectedTab !== "Week" || wellnessData.length === 0) {
      return {
        bestCategories: [],
        worstCategories: [],
        mostImproving: null,
        mostDeclining: null,
      };
    }

    const categories = [
      "health",
      "mood",
      "physical",
      "recovery",
      "sleep",
      "stress",
      "sleepTime",
    ];

    const categoryLabels: { [key: string]: string } = {
      health: "Health",
      mood: "Mood",
      physical: "Physical",
      recovery: "Recovery",
      sleep: "Sleep Quality",
      stress: "Stress Management",
      sleepTime: "Sleep Duration",
    };

    // Calculate averages for last 7 days (current week)
    const validData = wellnessData.filter((d) => d.hasData);
    const categoryAverages: { [key: string]: number } = {};

    categories.forEach((cat) => {
      const values = validData
        .map((d) => d[cat])
        .filter((v) => v !== undefined && v > 0);
      if (values.length > 0) {
        categoryAverages[cat] =
          values.reduce((sum, v) => sum + v, 0) / values.length;
      }
    });

    // Calculate averages for previous week (days 8-14)
    const validPreviousData = previousWeekData.filter((d) => d.hasData);
    const previousCategoryAverages: { [key: string]: number } = {};

    categories.forEach((cat) => {
      const values = validPreviousData
        .map((d) => d[cat])
        .filter((v) => v !== undefined && v > 0);
      if (values.length > 0) {
        previousCategoryAverages[cat] =
          values.reduce((sum, v) => sum + v, 0) / values.length;
      }
    });

    // Sort by average (higher is better, except for stress)
    const sortedByAverage = Object.entries(categoryAverages)
      .map(([cat, avg]) => ({
        category: cat,
        label: categoryLabels[cat],
        average: avg,
      }))
      .sort((a, b) => {
        // For stress, lower is better
        if (a.category === "stress") {
          return b.average - a.average;
        }
        if (b.category === "stress") {
          return a.average - b.average;
        }
        return b.average - a.average;
      });

    const bestCategories = sortedByAverage.slice(0, 2);
    const worstCategories = sortedByAverage.slice(-2).reverse();

    // Calculate changes (current week vs previous week)
    const categoryChanges = categories
      .map((cat) => {
        const currentAvg = categoryAverages[cat];
        const previousAvg = previousCategoryAverages[cat];

        // Skip if we don't have data for both periods
        if (
          currentAvg === undefined ||
          previousAvg === undefined ||
          previousAvg === 0
        ) {
          return null;
        }

        // For stress, improvement means decrease; for others, improvement means increase
        const change = currentAvg - previousAvg;
        const percentChange = (change / previousAvg) * 100;
        const isImprovement = cat === "stress" ? change < 0 : change > 0;

        return {
          category: cat,
          label: categoryLabels[cat],
          currentAvg,
          previousAvg,
          change,
          percentChange,
          isImprovement,
        };
      })
      .filter(
        (
          item,
        ): item is {
          category: string;
          label: string;
          currentAvg: number;
          previousAvg: number;
          change: number;
          percentChange: number;
          isImprovement: boolean;
        } => item !== null,
      );

    // Find most improving (largest positive change, or largest negative for stress)
    const improving = categoryChanges
      .filter((item) => item.isImprovement)
      .sort((a, b) => Math.abs(b.percentChange) - Math.abs(a.percentChange));

    const mostImproving = improving.length > 0 ? improving[0] : null;

    // Find most declining (largest negative change, or largest positive for stress)
    const declining = categoryChanges
      .filter((item) => !item.isImprovement)
      .sort((a, b) => Math.abs(b.percentChange) - Math.abs(a.percentChange));

    const mostDeclining = declining.length > 0 ? declining[0] : null;

    return {
      bestCategories,
      worstCategories,
      mostImproving,
      mostDeclining,
    };
  }, [selectedTab, wellnessData, previousWeekData]);

  // Calculate weekly averages for monthly view (4 weeks from 28 days)
  const monthlyWellnessInsights = useMemo(() => {
    if (selectedTab !== "Month") {
      return {
        bestCategories: [],
        worstCategories: [],
        mostImproving: null,
        mostDeclining: null,
      };
    }

    const categories = [
      "health",
      "mood",
      "physical",
      "recovery",
      "sleep",
      "stress",
      "sleepTime",
    ];

    const categoryLabels: { [key: string]: string } = {
      health: "Health",
      mood: "Mood",
      physical: "Physical",
      recovery: "Recovery",
      sleep: "Sleep Quality",
      stress: "Stress Management",
      sleepTime: "Sleep Duration",
    };

    // Calculate weekly averages for current month (4 weeks)
    const validData = monthlyData.filter((d) => d.hasData);
    const categoryAverages: { [key: string]: number } = {};

    categories.forEach((cat) => {
      const values = validData
        .map((d) => d[cat])
        .filter((v) => v !== undefined && v > 0);
      if (values.length > 0) {
        categoryAverages[cat] =
          values.reduce((sum, v) => sum + v, 0) / values.length;
      }
    });

    // Calculate weekly averages for previous month
    const validPreviousData = previousMonthlyData.filter((d) => d.hasData);
    const previousCategoryAverages: { [key: string]: number } = {};

    categories.forEach((cat) => {
      const values = validPreviousData
        .map((d) => d[cat])
        .filter((v) => v !== undefined && v > 0);
      if (values.length > 0) {
        previousCategoryAverages[cat] =
          values.reduce((sum, v) => sum + v, 0) / values.length;
      }
    });

    // Sort by average
    const sortedByAverage = Object.entries(categoryAverages)
      .map(([cat, avg]) => ({
        category: cat,
        label: categoryLabels[cat],
        average: avg,
      }))
      .sort((a, b) => {
        if (a.category === "stress") {
          return b.average - a.average;
        }
        if (b.category === "stress") {
          return a.average - b.average;
        }
        return b.average - a.average;
      });

    const bestCategories = sortedByAverage.slice(0, 2);
    const worstCategories = sortedByAverage.slice(-2).reverse();

    // Calculate changes
    const categoryChanges = categories
      .map((cat) => {
        const currentAvg = categoryAverages[cat];
        const previousAvg = previousCategoryAverages[cat];

        if (
          currentAvg === undefined ||
          previousAvg === undefined ||
          previousAvg === 0
        ) {
          return null;
        }

        const change = currentAvg - previousAvg;
        const percentChange = (change / previousAvg) * 100;
        const isImprovement = cat === "stress" ? change < 0 : change > 0;

        return {
          category: cat,
          label: categoryLabels[cat],
          currentAvg,
          previousAvg,
          change,
          percentChange,
          isImprovement,
        };
      })
      .filter(
        (
          item,
        ): item is {
          category: string;
          label: string;
          currentAvg: number;
          previousAvg: number;
          change: number;
          percentChange: number;
          isImprovement: boolean;
        } => item !== null,
      );

    const improving = categoryChanges
      .filter((item) => item.isImprovement)
      .sort((a, b) => Math.abs(b.percentChange) - Math.abs(a.percentChange));

    const mostImproving = improving.length > 0 ? improving[0] : null;

    const declining = categoryChanges
      .filter((item) => !item.isImprovement)
      .sort((a, b) => Math.abs(b.percentChange) - Math.abs(a.percentChange));

    const mostDeclining = declining.length > 0 ? declining[0] : null;

    return {
      bestCategories,
      worstCategories,
      mostImproving,
      mostDeclining,
    };
  }, [selectedTab, monthlyData, previousMonthlyData]);

  // Calculate weekly averages for quarterly view (12 weeks from 84 days)
  const quarterlyWellnessInsights = useMemo(() => {
    if (selectedTab !== "Quarter") {
      return {
        bestCategories: [],
        worstCategories: [],
        mostImproving: null,
        mostDeclining: null,
      };
    }

    const categories = [
      "health",
      "mood",
      "physical",
      "recovery",
      "sleep",
      "stress",
      "sleepTime",
    ];

    const categoryLabels: { [key: string]: string } = {
      health: "Health",
      mood: "Mood",
      physical: "Physical",
      recovery: "Recovery",
      sleep: "Sleep Quality",
      stress: "Stress Management",
      sleepTime: "Sleep Duration",
    };

    // Calculate overall averages for current quarter (for Best/Worst categories)
    const validData = quarterlyData.filter((d) => d.hasData);
    const categoryAverages: { [key: string]: number } = {};

    categories.forEach((cat) => {
      const values = validData
        .map((d) => d[cat])
        .filter((v) => v !== undefined && v > 0);
      if (values.length > 0) {
        categoryAverages[cat] =
          values.reduce((sum, v) => sum + v, 0) / values.length;
      }
    });

    // Sort by average for Best/Worst categories
    const sortedByAverage = Object.entries(categoryAverages)
      .map(([cat, avg]) => ({
        category: cat,
        label: categoryLabels[cat],
        average: avg,
      }))
      .sort((a, b) => {
        if (a.category === "stress") {
          return b.average - a.average;
        }
        if (b.category === "stress") {
          return a.average - b.average;
        }
        return b.average - a.average;
      });

    const bestCategories = sortedByAverage.slice(0, 2);
    const worstCategories = sortedByAverage.slice(-2).reverse();

    // Calculate weekly averages for each category across 12 weeks
    const categoryWeeklyTrends = categories
      .map((cat) => {
        const weeklyAverages: number[] = [];

        for (let week = 0; week < 12; week++) {
          const weekStart = week * 7;
          const weekEnd = weekStart + 7;
          const weekData = quarterlyData.slice(weekStart, weekEnd);

          const validWeekData = weekData.filter(
            (d) => d.hasData && d[cat] !== undefined && d[cat] > 0,
          );

          if (validWeekData.length > 0) {
            const avg =
              validWeekData.reduce((sum, d) => sum + (d[cat] || 0), 0) /
              validWeekData.length;
            weeklyAverages.push(avg);
          }
        }

        // Calculate trend: difference between last week average and first week average
        if (weeklyAverages.length >= 2) {
          const firstWeekAvg = weeklyAverages[0];
          const lastWeekAvg = weeklyAverages[weeklyAverages.length - 1];
          const change = lastWeekAvg - firstWeekAvg;
          const percentChange = (change / firstWeekAvg) * 100;

          // For all categories including stress, higher is better (stress score represents low stress)
          const isImprovement = change > 0;

          return {
            category: cat,
            label: categoryLabels[cat],
            currentAvg: lastWeekAvg,
            previousAvg: firstWeekAvg, // First week average as baseline
            change,
            percentChange,
            isImprovement,
            weeklyAverages,
          };
        }

        return null;
      })
      .filter(
        (
          item,
        ): item is {
          category: string;
          label: string;
          currentAvg: number;
          previousAvg: number;
          change: number;
          percentChange: number;
          isImprovement: boolean;
          weeklyAverages: number[];
        } => item !== null,
      );

    // Find most improving: largest positive change (or largest negative for stress)
    const improving = categoryWeeklyTrends
      .filter((item) => item.isImprovement)
      .sort((a, b) => Math.abs(b.percentChange) - Math.abs(a.percentChange));

    const mostImproving = improving.length > 0 ? improving[0] : null;

    // Find most declining: largest negative change (or largest positive for stress)
    const declining = categoryWeeklyTrends
      .filter((item) => !item.isImprovement)
      .sort((a, b) => Math.abs(b.percentChange) - Math.abs(a.percentChange));

    const mostDeclining = declining.length > 0 ? declining[0] : null;

    return {
      bestCategories,
      worstCategories,
      mostImproving,
      mostDeclining,
    };
  }, [selectedTab, quarterlyData]);

  // Calculate average score based on selected tab
  const averageScore = useMemo(() => {
    if (selectedTab === "Day") {
      return currentDayWellness?.wellnessScore || 0;
    } else if (selectedTab === "Week") {
      const scoresWithData = wellnessData.filter((d) => d.hasData);
      if (scoresWithData.length === 0) return 0;
      return (
        scoresWithData.reduce((sum, d) => sum + d.score, 0) /
        scoresWithData.length
      );
    } else if (selectedTab === "Month") {
      const scoresWithData = monthlyData.filter((d) => d.hasData);
      if (scoresWithData.length === 0) return 0;
      return (
        scoresWithData.reduce((sum, d) => sum + d.score, 0) /
        scoresWithData.length
      );
    } else if (selectedTab === "Quarter") {
      const scoresWithData = quarterlyData.filter((d) => d.hasData);
      if (scoresWithData.length === 0) return 0;
      return (
        scoresWithData.reduce((sum, d) => sum + d.score, 0) /
        scoresWithData.length
      );
    } else if (selectedTab === "Custom") {
      const scoresWithData = customRangeData.filter((d) => d.hasData);
      if (scoresWithData.length === 0) return 0;
      return (
        scoresWithData.reduce((sum, d) => sum + d.score, 0) /
        scoresWithData.length
      );
    }
    return 0;
  }, [
    selectedTab,
    currentDayWellness,
    wellnessData,
    monthlyData,
    quarterlyData,
    customRangeData,
  ]);

  // Determine which color scheme to use (green for Best Performing/Most Improving, red for Needs Attention/Most Declining)
  const colorScheme = useMemo(() => {
    const lineChartParams = selectedParameters.filter(
      (paramId) =>
        paramId !== "score" &&
        paramId !== "injured" &&
        paramId !== "sick" &&
        paramId !== "niggle",
    );

    // Select the appropriate insights based on tab
    let insights = weeklyWellnessInsights;
    if (selectedTab === "Month") {
      insights = monthlyWellnessInsights;
    } else if (selectedTab === "Quarter") {
      insights = quarterlyWellnessInsights;
    }

    // Check if current selection matches Best Performing categories
    if (insights.bestCategories.length > 0) {
      const bestCats = insights.bestCategories.map((c) => c.category);
      const matchesBest =
        bestCats.length === lineChartParams.length &&
        bestCats.every((cat) => lineChartParams.includes(cat));
      if (matchesBest) return "green";
    }

    // Check if current selection matches Needs Attention categories
    if (insights.worstCategories.length > 0) {
      const worstCats = insights.worstCategories.map((c) => c.category);
      const matchesWorst =
        worstCats.length === lineChartParams.length &&
        worstCats.every((cat) => lineChartParams.includes(cat));
      if (matchesWorst) return "red";
    }

    // Check if current selection matches Most Improving category
    if (
      insights.mostImproving &&
      lineChartParams.length === 1 &&
      lineChartParams[0] === insights.mostImproving.category
    ) {
      return "green";
    }

    // Check if current selection matches Most Declining category
    if (
      insights.mostDeclining &&
      lineChartParams.length === 1 &&
      lineChartParams[0] === insights.mostDeclining.category
    ) {
      return "red";
    }

    return "default";
  }, [
    selectedParameters,
    selectedTab,
    weeklyWellnessInsights,
    monthlyWellnessInsights,
    quarterlyWellnessInsights,
  ]);

  // Calculate 7-day averages for each wellness category (for Day view markers)
  const sevenDayAverages = useMemo(() => {
    if (wellnessData.length === 0) {
      return {};
    }

    const categories = [
      "health",
      "mood",
      "physical",
      "recovery",
      "sleep",
      "stress",
      "sleepTime",
      "soreness",
    ];

    const averages: { [key: string]: number } = {};
    const validData = wellnessData.filter((d) => d.hasData);

    categories.forEach((cat) => {
      const values = validData
        .map((d) => d[cat])
        .filter((v) => v !== undefined && v > 0);
      if (values.length > 0) {
        averages[cat] = values.reduce((sum, v) => sum + v, 0) / values.length;
      }
    });

    return averages;
  }, [wellnessData]);

  // Apply colors to line chart based on selection (green for Best Performing/Most Improving, red for Needs Attention/Most Declining)
  const transformDataWithColors = useMemo(() => {
    const greenShades = ["#4ade80", "#15803d"]; // green-400, green-700
    const redShades = ["#f87171", "#b91c1c"]; // red-400, red-700

    // Select the appropriate data based on tab
    let dataToTransform = transformDataForLineChart;
    if (selectedTab === "Month") {
      dataToTransform =
        monthViewMode === "daily"
          ? transformMonthlyDailyDataForLineChart
          : transformMonthlyDataForLineChart;
    } else if (selectedTab === "Quarter") {
      dataToTransform = transformQuarterlyDataForLineChart;
    }

    // Apply green colors for Best Performing or Most Improving
    if (colorScheme === "green") {
      return dataToTransform.map((series, index) => ({
        ...series,
        color: greenShades[index % greenShades.length],
      }));
    }

    // Apply red colors for Needs Attention or Most Declining
    if (colorScheme === "red") {
      return dataToTransform.map((series, index) => ({
        ...series,
        color: redShades[index % redShades.length],
      }));
    }

    return dataToTransform;
  }, [
    transformDataForLineChart,
    transformMonthlyDataForLineChart,
    transformMonthlyDailyDataForLineChart,
    transformQuarterlyDataForLineChart,
    colorScheme,
    selectedTab,
    monthViewMode,
  ]);

  // Dynamic gradient definitions based on color scheme (green, red, or default)
  const gradientDefs = useMemo(() => {
    const greenShades = ["#22c55e", "#16a34a"]; // green-500, green-600
    const redShades = ["#ef4444", "#dc2626"]; // red-500, red-600

    if (colorScheme === "green") {
      // Create green gradients for the selected series
      return transformDataWithColors.map((series, index) => ({
        id: `gradient${index}`,
        type: "linearGradient" as const,
        colors: [
          {
            offset: 0,
            color: greenShades[index % greenShades.length],
            opacity: 0.4,
          },
          {
            offset: 100,
            color: greenShades[index % greenShades.length],
            opacity: 0.1,
          },
        ],
      }));
    }

    if (colorScheme === "red") {
      // Create red gradients for the selected series
      return transformDataWithColors.map((series, index) => ({
        id: `gradient${index}`,
        type: "linearGradient" as const,
        colors: [
          {
            offset: 0,
            color: redShades[index % redShades.length],
            opacity: 0.4,
          },
          {
            offset: 100,
            color: redShades[index % redShades.length],
            opacity: 0.1,
          },
        ],
      }));
    }

    // Default gradients for all parameters
    return [
      {
        id: "gradientScore",
        type: "linearGradient" as const,
        colors: [
          { offset: 0, color: "#059669", opacity: 0.4 },
          { offset: 100, color: "#059669", opacity: 0.1 },
        ],
      },
      {
        id: "gradientHealth",
        type: "linearGradient" as const,
        colors: [
          { offset: 0, color: "#166534", opacity: 0.4 },
          { offset: 100, color: "#166534", opacity: 0.1 },
        ],
      },
      {
        id: "gradientMood",
        type: "linearGradient" as const,
        colors: [
          { offset: 0, color: "#ea580c", opacity: 0.4 },
          { offset: 100, color: "#ea580c", opacity: 0.1 },
        ],
      },
      {
        id: "gradientPhysical",
        type: "linearGradient" as const,
        colors: [
          { offset: 0, color: "#dc2626", opacity: 0.4 },
          { offset: 100, color: "#dc2626", opacity: 0.1 },
        ],
      },
      {
        id: "gradientRecovery",
        type: "linearGradient" as const,
        colors: [
          { offset: 0, color: "#1e40af", opacity: 0.4 },
          { offset: 100, color: "#1e40af", opacity: 0.1 },
        ],
      },
      {
        id: "gradientSleep",
        type: "linearGradient" as const,
        colors: [
          { offset: 0, color: "#581c87", opacity: 0.4 },
          { offset: 100, color: "#581c87", opacity: 0.1 },
        ],
      },
      {
        id: "gradientStress",
        type: "linearGradient" as const,
        colors: [
          { offset: 0, color: "#ef4444", opacity: 0.4 },
          { offset: 100, color: "#ef4444", opacity: 0.1 },
        ],
      },
      {
        id: "gradientSleepTime",
        type: "linearGradient" as const,
        colors: [
          { offset: 0, color: "#7c3aed", opacity: 0.4 },
          { offset: 100, color: "#7c3aed", opacity: 0.1 },
        ],
      },
    ];
  }, [colorScheme, transformDataWithColors]);

  // Dynamic fill mappings based on color scheme (green, red, or default)
  const fillMappings = useMemo(() => {
    if (colorScheme === "green" || colorScheme === "red") {
      // Map each series to its corresponding gradient (green or red)
      return transformDataWithColors.map((series, index) => ({
        match: { id: series.id },
        id: `gradient${index}`,
      }));
    }

    // Default fill mappings
    return [
      {
        match: { id: "Wellness Score" },
        id: "gradientScore",
      },
      { match: { id: "Health" }, id: "gradientHealth" },
      { match: { id: "Mood" }, id: "gradientMood" },
      { match: { id: "Physical" }, id: "gradientPhysical" },
      { match: { id: "Recovery" }, id: "gradientRecovery" },
      {
        match: { id: "Sleep Quality" },
        id: "gradientSleep",
      },
      { match: { id: "Stress Management" }, id: "gradientStress" },
      {
        match: { id: "Sleep Duration" },
        id: "gradientSleepTime",
      },
    ];
  }, [colorScheme, transformDataWithColors]);

  // Get the display color for a parameter (used in legend)
  const getParameterColor = (paramId: string): string => {
    // Wellness score always uses gray to match the bar chart
    if (paramId === "score") {
      return isDark ? "#ffffff" : "#374151"; // gray-700 in light, white in dark mode
    }

    // Find the color from transformDataWithColors if it's currently selected
    const series = transformDataWithColors.find((s) => {
      const param = availableParameters.find((p) => p.label === s.id);
      return param?.id === paramId;
    });

    if (series) {
      return series.color;
    }

    // Otherwise return the default color
    const param = availableParameters.find((p) => p.id === paramId);
    return param?.color || "#000000";
  };

  // Handle back navigation - return to dashboard with the same date
  const handleBack = () => {
    const dateID = generateDateID(targetDate);
    // navigate(`/dashboard?date=${dateID}`);
    navigate(-1);
  };

  return (
    <div className="w-full min-h-screen bg-base-200 pt-16">
      <Header />

      {/* Header */}
      <div className="shadow-sm">
        <div className="mx-auto max-w-4xl px-4 py-4">
          <div className="flex w-full items-center justify-between">
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleBack}
                  className="p-2 rounded-full hover:bg-base-200 transition-color bg-base-300"
                >
                  <ChevronLeftIcon className="h-5 w-5 text-base-content/80" />
                </button>
                <h1 className="text-2xl font-grotesk font-bold text-base-content">
                  Wellness
                </h1>
              </div>
              <div className="flex space-x-2">
                {/* Export button hidden - feature not available yet */}
                {/* <button
                  onClick={() => setShowExportDialog(true)}
                  className="w-8 h-8 rounded-full bg-base-300 hover:bg-base-300 flex items-center justify-center text-base-content/70 hover:text-base-content transition-colors"
                  title="Export wellness data"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 text-base-content/60" />
                </button> */}
                <button
                  onClick={() => setShowInfoDialog(true)}
                  className="w-8 h-8 rounded-full bg-base-300 hover:bg-base-300 flex items-center justify-center text-base-content/70 hover:text-base-content transition-colors"
                  title="Learn more about Health and Sleep"
                >
                  ?
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Tab Navigation */}
      {/* Tab Navigation */}
      <div className="mb-0 mx-auto max-w-4xl">
        <div className="flex justify-around border-b border-base-300">
          {(["Day", "Week", "Month", "Quarter"] as const).map((tab) => {
            // Determine if this tab is restricted for the current user
            const isRestricted =
              (tab === "Month" && !hasMonthlyAccess) ||
              (tab === "Quarter" && !hasQuarterlyAccess);

            return (
              <button
                key={tab}
                onClick={() => {
                  if (isRestricted) {
                    // Open premium modal/tooltip
                    setShowInfoDialog(false);
                    // Reusing existing ComingSoon modal logic via restricted content check
                    // Or simply do nothing to prevent tab switch, letting the blur handle it
                  }
                  setSelectedTab(tab);
                }}
                className={`relative py-3 px-4 text-sm font-medium font-grotesk transition-colors duration-200 flex-1 text-center flex items-center justify-center gap-1 ${
                  selectedTab === tab
                    ? "text-base-content border-b-2 border-base-content bg-base-300"
                    : "text-base-content/60 hover:text-base-content/80"
                }`}
              >
                {tab}
                {isRestricted && (
                  <span className="text-blue-500">
                    <LockClosedIcon className="w-3 h-3" />
                  </span>
                )}
              </button>
            );
          })}
          {/* Custom Date Range Icon Button */}
          <button
            onClick={() => setShowCustomDateModal(true)}
            className={`relative py-3 px-4 text-sm font-medium font-grotesk transition-colors duration-200 text-center flex items-center justify-center gap-1 ${
              selectedTab === "Custom"
                ? "text-base-content border-b-2 border-base-content bg-base-300"
                : "text-base-content/60 hover:text-base-content/80"
            }`}
            title="Custom Date Range"
          >
            <CalendarIcon
              className={`h-5 w-5 ${
                selectedTab === "Custom"
                  ? "text-base-content"
                  : "text-base-content/60"
              }`}
            />
            {!hasCustomDateAccess && (
              <span className="text-blue-500">
                <LockClosedIcon className="w-3 h-3" />
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="mx-auto max-w-4xl">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="flex flex-col items-center gap-6">
              {/* Large animated chevron */}
              <h1 className="text-base-content text-[8rem] font-groteskLogo animate-pulse-custom relative z-10">
                ^
              </h1>

              {/* Loading text */}
              <div className="text-center -mt-16">
                <p className="text-lg font-medium text-base-content font-grotesk">
                  Loading Wellness data...
                </p>
                <p className="text-sm text-base-content/70 font-grotesk mt-2">
                  Analyzing your wellness metrics
                </p>
              </div>

              {/* Loading progress indicator */}
              <div className="flex items-center gap-2 mt-4">
                <div className="w-32 h-1 bg-base-300 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full animate-pulse transition-all duration-1000 w-3/4"></div>
                </div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Content */}
          <div className="mx-auto max-w-4xl">
            {/* Content for all views */}
            <div
              className={
                selectedTab === "Quarter" || selectedTab === "Month"
                  ? "relative"
                  : ""
              }
            >
              {/* Show Premium Modal trigger for Quarter if restricted */}
              {!hasQuarterlyAccess && selectedTab === "Quarter" && (
                <div className="absolute inset-0 z-10 flex items-start justify-center pt-20">
                  <ComingSoon featureKey="advancedGraphs" />
                </div>
              )}
              {/* Monthly is now unlocked for Plus, so we remove the !hasMonthlyAccess check here */}
              <div
                className={
                  !hasQuarterlyAccess && selectedTab === "Quarter"
                    ? "blur-md pointer-events-none"
                    : ""
                }
              >
                {/* Main Score Display - only shown on Day view */}
                {selectedTab === "Day" && !currentDayWellness && !loading && (
                  <div className="mb-2 p-4 px-8">
                    <div className="flex flex-col items-center justify-center py-12">
                      <p className="text-lg text-base-content/70 font-grotesk mb-6">
                        No wellness data logged for today
                      </p>
                      <button
                        onClick={() => setShowWellnessForm(true)}
                        className="px-6 py-3 bg-primary text-white rounded-xl font-grotesk font-medium hover:opacity-90 transition-opacity"
                      >
                        Log Wellness
                      </button>
                    </div>
                  </div>
                )}

                {selectedTab === "Day" && currentDayWellness && (
                  <div className="mb-2 p-4 px-8">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-6xl font-bold font-heading text-base-content mb-2">
                          {Math.round(averageScore)}
                        </div>
                        <div className="text-lg text-base-content/70 font-grotesk mb-1">
                          Today's Wellness Score
                        </div>
                      </div>
                      {/* Share button IS REMOVED FOR NOW AS IT LOOKS SHIT ON IOS*/}
                      <button
                        className="text-xs px-3 py-3 bg-neutral text-white hover:opacity-90 flex items-center space-x-1 rounded-full"
                        onClick={() => setWellnessExportOpen(true)}
                        title="Share wellness data"
                      >
                        <ArrowUpTrayIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* No data messages for Week/Month/Quarter */}
                {selectedTab === "Week" &&
                  !loading &&
                  wellnessData.filter((d) => d.hasData).length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 px-8">
                      <p className="text-lg text-base-content/70 font-grotesk">
                        No wellness data for this week
                      </p>
                    </div>
                  )}

                {selectedTab === "Month" &&
                  !loading &&
                  monthlyData.filter((d) => d.hasData).length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 px-8">
                      <p className="text-lg text-base-content/70 font-grotesk">
                        No wellness data for this month
                      </p>
                    </div>
                  )}

                {selectedTab === "Quarter" &&
                  !loading &&
                  quarterlyData.filter((d) => d.hasData).length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 px-8">
                      <p className="text-lg text-base-content/70 font-grotesk">
                        No wellness data for this quarter
                      </p>
                    </div>
                  )}

                {error && (
                  <div className="bg-red-500/10 border border-red-200 rounded-lg p-4 mb-6">
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}

                {/* Day View: Parameter Breakdown */}
                {selectedTab === "Day" && currentDayWellness && (
                  <div className="px-4 pb-4 space-y-3">
                    {/* 7-day average toggle */}
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-grotesk font-semibold text-base-content">
                        Breakdown
                      </h2>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-xs text-base-content/60 font-geist">
                          7-day avg
                        </span>
                        <input
                          type="checkbox"
                          className="toggle toggle-xs toggle-primary"
                          checked={showSevenDayComparison}
                          onChange={() =>
                            setShowSevenDayComparison(!showSevenDayComparison)
                          }
                        />
                      </label>
                    </div>
                    {/* Parameter cards */}
                    {[
                      {
                        key: "health",
                        label: "Health",
                        value: currentDayWellness.health,
                        max: 7,
                      },
                      {
                        key: "mood",
                        label: "Mood",
                        value: currentDayWellness.mood,
                        max: 7,
                      },
                      {
                        key: "physical",
                        label: "Physical",
                        value: currentDayWellness.physical,
                        max: 7,
                      },
                      {
                        key: "recovery",
                        label: "Recovery",
                        value: currentDayWellness.recovery,
                        max: 7,
                      },
                      {
                        key: "sleep",
                        label: "Sleep Quality",
                        value: currentDayWellness.sleep,
                        max: 7,
                      },
                      {
                        key: "stress",
                        label: "Stress",
                        value: currentDayWellness.stress,
                        max: 7,
                      },
                      {
                        key: "sleepTime",
                        label: "Sleep Duration",
                        value: currentDayWellness.sleepTime,
                        max: 7,
                      },
                      {
                        key: "soreness",
                        label: "Soreness",
                        value: currentDayWellness.soreness,
                        max: 10,
                      },
                    ].map((param) => {
                      const val = param.value || 0;
                      const pct = (val / param.max) * 100;
                      const avg = sevenDayAverages[param.key];
                      const color =
                        param.key === "soreness"
                          ? getSorenessColor(val)
                          : getWellnessColor(val);
                      return (
                        <div
                          key={param.key}
                          className="bg-base-200/50 rounded-xl p-3"
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-geist font-medium text-base-content">
                              {param.label}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-grotesk font-bold text-base-content">
                                {val}
                              </span>
                              <span className="text-xs text-base-content/40 font-geist">
                                /{param.max}
                              </span>
                              {showSevenDayComparison && avg !== undefined && (
                                <span className="text-xs text-base-content/50 font-geist ml-1">
                                  (avg {avg.toFixed(1)})
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="relative w-full h-2 bg-base-300 rounded-full overflow-hidden">
                            <div
                              className="absolute top-0 left-0 h-full rounded-full transition-all duration-500"
                              style={{
                                width: `${pct}%`,
                                backgroundColor: color,
                              }}
                            />
                            {showSevenDayComparison && avg !== undefined && (
                              <div
                                className="absolute top-0 h-full w-0.5 bg-base-content/40"
                                style={{ left: `${(avg / param.max) * 100}%` }}
                                title={`7-day avg: ${avg.toFixed(1)}`}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {/* Injury / Sick flags */}
                    {(currentDayWellness.injured ||
                      currentDayWellness.sick) && (
                      <div className="flex gap-2 mt-2">
                        {currentDayWellness.injured && (
                          <span className="px-3 py-1 rounded-full text-xs font-geist font-medium bg-red-500/10 text-red-600 border border-red-500/20">
                            Injured
                          </span>
                        )}
                        {currentDayWellness.sick && (
                          <span className="px-3 py-1 rounded-full text-xs font-geist font-medium bg-amber-500/10 text-amber-600 border border-amber-500/20">
                            Unwell
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Week/Month/Quarter/Custom Chart Section */}
                {selectedTab !== "Day" && (
                  <div className="px-4 pb-4">
                    {/* Parameter Selector */}
                    <div
                      className="mb-3 mt-3 relative flex items-center justify-between"
                      ref={dropdownRef}
                    >
                      <button
                        onClick={() =>
                          setShowParameterDropdown(!showParameterDropdown)
                        }
                        className="flex items-center gap-2 px-3 py-2 bg-base-200 rounded-lg text-sm font-geist text-base-content/80 hover:bg-base-300 transition-colors"
                      >
                        <span>Parameters ({selectedParameters.length})</span>
                        <ChevronDownIcon
                          className={`h-4 w-4 transition-transform ${showParameterDropdown ? "rotate-180" : ""}`}
                        />
                      </button>
                      {selectedParameters.length > 1 ||
                      !selectedParameters.includes("score") ? (
                        <button
                          onClick={() => setSelectedParameters(["score"])}
                          className="px-3 py-2 bg-base-200 rounded-lg text-sm font-geist text-base-content/60 hover:bg-base-300 hover:text-base-content/80 transition-colors"
                        >
                          Reset
                        </button>
                      ) : null}
                      {showParameterDropdown && (
                        <div className="absolute top-full left-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg z-20 w-64 max-h-80 overflow-y-auto">
                          {availableParameters.map((param) => (
                            <label
                              key={param.id}
                              className="flex items-center gap-3 px-3 py-2 hover:bg-base-200 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                className="checkbox checkbox-xs"
                                checked={selectedParameters.includes(param.id)}
                                onChange={() => {
                                  setSelectedParameters((prev) =>
                                    prev.includes(param.id)
                                      ? prev.filter((p) => p !== param.id)
                                      : [...prev, param.id],
                                  );
                                }}
                              />
                              <span
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{
                                  backgroundColor: getParameterColor(param.id),
                                }}
                              />
                              <span className="text-sm font-geist text-base-content">
                                {param.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Month view mode toggle */}
                    {selectedTab === "Month" && (
                      <div className="flex items-center gap-3 bg-base-200 rounded-lg p-1 mb-3 w-fit">
                        <button
                          onClick={() => setMonthViewMode("daily")}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                            monthViewMode === "daily"
                              ? "bg-base-100 text-base-content shadow-sm"
                              : "text-base-content/70 hover:text-base-content"
                          }`}
                        >
                          Daily
                        </button>
                        <button
                          onClick={() => setMonthViewMode("weekly")}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                            monthViewMode === "weekly"
                              ? "bg-base-100 text-base-content shadow-sm"
                              : "text-base-content/70 hover:text-base-content"
                          }`}
                        >
                          Weekly
                        </button>
                      </div>
                    )}

                    {/* Chart */}
                    {(transformDataWithColors.length > 0 ||
                      (() => {
                        if (selectedTab === "Week")
                          return transformDataForBars.length > 0;
                        if (selectedTab === "Month")
                          return monthViewMode === "daily"
                            ? transformMonthlyDailyDataForBars.length > 0
                            : transformMonthlyDataForBars.length > 0;
                        if (selectedTab === "Quarter")
                          return transformQuarterlyDataForBars.length > 0;
                        return false;
                      })()) && (
                      <div className=" p-2">
                        {/* Selected parameter legend */}
                        <div className="flex flex-wrap gap-2 px-4 pt-3 pb-2">
                          {selectedParameters.map((paramId) => {
                            const param = availableParameters.find(
                              (p) => p.id === paramId,
                            );
                            if (!param) return null;
                            return (
                              <span
                                key={paramId}
                                className="flex items-center gap-1.5 text-xs font-geist text-base-content/70"
                              >
                                <span
                                  className="w-2.5 h-2.5 rounded-full"
                                  style={{
                                    backgroundColor: getParameterColor(paramId),
                                  }}
                                />
                                {param.label}
                              </span>
                            );
                          })}
                        </div>
                        <div style={{ height: isMobile ? 260 : 320 }}>
                          <ResponsiveLine
                            data={
                              transformDataWithColors.length > 0
                                ? transformDataWithColors
                                : [
                                    {
                                      id: "placeholder",
                                      data: [{ x: "0", y: 0 }],
                                      color: "transparent",
                                    },
                                  ]
                            }
                            theme={nivoTheme}
                            margin={{
                              top: 20,
                              right: 20,
                              bottom: 40,
                              left: 40,
                            }}
                            xScale={{ type: "point" }}
                            yScale={{
                              type: "linear",
                              min: 0,
                              max: 100,
                              stacked: false,
                            }}
                            curve="monotoneX"
                            axisBottom={{
                              tickSize: 0,
                              tickPadding: 8,
                              tickRotation: isMobile ? -45 : 0,
                            }}
                            axisLeft={{
                              tickSize: 0,
                              tickPadding: 8,
                              tickValues: [0, 25, 50, 75, 100],
                            }}
                            enableGridX={false}
                            gridYValues={[0, 25, 50, 75, 100]}
                            colors={(d) => d.color || "#059669"}
                            lineWidth={2}
                            pointSize={6}
                            pointBorderWidth={2}
                            pointBorderColor={{ from: "serieColor" }}
                            pointColor="white"
                            enableArea={true}
                            areaOpacity={0.15}
                            defs={gradientDefs}
                            fill={fillMappings}
                            useMesh={true}
                            enableSlices="x"
                            sliceTooltip={({ slice }) => {
                              const xLabel =
                                slice.points[0]?.data.xFormatted ?? "";
                              // Find the wellness score for this x from bar data
                              const barData = (() => {
                                if (
                                  selectedTab === "Week" ||
                                  selectedTab === "Custom"
                                )
                                  return transformDataForBars;
                                if (selectedTab === "Month")
                                  return monthViewMode === "daily"
                                    ? transformMonthlyDailyDataForBars
                                    : transformMonthlyDataForBars;
                                if (selectedTab === "Quarter")
                                  return transformQuarterlyDataForBars;
                                return [];
                              })();
                              const barPoint = barData.find(
                                (d: any) => d.day === xLabel,
                              );
                              return (
                                <div
                                  style={{
                                    background: isDark ? "#1f2937" : "#ffffff",
                                    border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                                    borderRadius: 8,
                                    padding: "8px 12px",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                    minWidth: 140,
                                  }}
                                >
                                  <p
                                    style={{
                                      margin: 0,
                                      fontWeight: 700,
                                      fontSize: 12,
                                      color: isDark ? "#e5e5e5" : "#111827",
                                      marginBottom: 6,
                                    }}
                                  >
                                    {xLabel}
                                  </p>
                                  {barPoint &&
                                    selectedParameters.includes("score") && (
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 6,
                                          marginBottom: 4,
                                        }}
                                      >
                                        <span
                                          style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            background: "#059669",
                                            flexShrink: 0,
                                          }}
                                        />
                                        <span
                                          style={{
                                            fontSize: 11,
                                            color: isDark
                                              ? "#d1d5db"
                                              : "#374151",
                                          }}
                                        >
                                          Wellness Score:{" "}
                                          <strong>
                                            {Math.round(barPoint.score)}
                                          </strong>
                                        </span>
                                      </div>
                                    )}
                                  {slice.points
                                    .filter(
                                      (point) =>
                                        point.seriesId !== "Wellness Score",
                                    )
                                    .map((point) => (
                                      <div
                                        key={point.id}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 6,
                                          marginBottom: 2,
                                        }}
                                      >
                                        <span
                                          style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            background: point.seriesColor,
                                            flexShrink: 0,
                                          }}
                                        />
                                        <span
                                          style={{
                                            fontSize: 11,
                                            color: isDark
                                              ? "#d1d5db"
                                              : "#374151",
                                          }}
                                        >
                                          {point.seriesId}:{" "}
                                          <strong>
                                            {Math.round(
                                              Number(point.data.yFormatted),
                                            )}
                                          </strong>
                                        </span>
                                      </div>
                                    ))}
                                </div>
                              );
                            }}
                            layers={[
                              "grid",
                              "markers",
                              "areas",
                              // Bar layer for wellness score
                              (props) => {
                                const barData = (() => {
                                  if (
                                    selectedTab === "Week" ||
                                    selectedTab === "Custom"
                                  )
                                    return transformDataForBars;
                                  if (selectedTab === "Month")
                                    return monthViewMode === "daily"
                                      ? transformMonthlyDailyDataForBars
                                      : transformMonthlyDataForBars;
                                  if (selectedTab === "Quarter")
                                    return transformQuarterlyDataForBars;
                                  return [];
                                })();
                                if (
                                  !selectedParameters.includes("score") ||
                                  barData.length === 0
                                )
                                  return null;
                                const { innerWidth, innerHeight } = props;
                                const barWidth = Math.max(
                                  4,
                                  Math.min(20, innerWidth / barData.length - 4),
                                );
                                return (
                                  <g>
                                    {barData.map((d: any, i: number) => {
                                      const x =
                                        (i / (barData.length - 1 || 1)) *
                                        innerWidth;
                                      const barH =
                                        (d.score / 100) * innerHeight;
                                      const onlyScore =
                                        selectedParameters.length === 1 &&
                                        selectedParameters[0] === "score";
                                      const barColor = onlyScore
                                        ? isDark
                                          ? "rgba(34,197,94,0.5)"
                                          : "rgba(34,197,94,0.6)"
                                        : isDark
                                          ? "rgba(255,255,255,0.15)"
                                          : "rgba(0,0,0,0.08)";
                                      return (
                                        <rect
                                          key={`bar-${i}`}
                                          x={x - barWidth / 2}
                                          y={innerHeight - barH}
                                          width={barWidth}
                                          height={barH}
                                          fill={barColor}
                                          rx={2}
                                        />
                                      );
                                    })}
                                  </g>
                                );
                              },
                              "lines",
                              "points",
                              "slices",
                              // Flag layer for injury/sick/niggle
                              (props) => {
                                if (flagData.length === 0) return null;
                                const { innerWidth, innerHeight } = props;
                                return (
                                  <g>
                                    {flagData.map((d: any, i: number) => {
                                      const x =
                                        (i / (flagData.length - 1 || 1)) *
                                        innerWidth;
                                      const icons: React.ReactElement[] = [];
                                      if (
                                        d.injured &&
                                        selectedParameters.includes("injured")
                                      ) {
                                        icons.push(
                                          <circle
                                            key={`inj-${i}`}
                                            cx={x}
                                            cy={innerHeight + 22}
                                            r={4}
                                            fill="#dc2626"
                                          />,
                                        );
                                      }
                                      if (
                                        d.sick &&
                                        selectedParameters.includes("sick")
                                      ) {
                                        icons.push(
                                          <circle
                                            key={`sick-${i}`}
                                            cx={x + (d.injured ? 10 : 0)}
                                            cy={innerHeight + 22}
                                            r={4}
                                            fill="#f59e0b"
                                          />,
                                        );
                                      }
                                      if (
                                        d.niggle &&
                                        selectedParameters.includes("niggle")
                                      ) {
                                        icons.push(
                                          <circle
                                            key={`nig-${i}`}
                                            cx={
                                              x +
                                              (d.injured ? 10 : 0) +
                                              (d.sick ? 10 : 0)
                                            }
                                            cy={innerHeight + 22}
                                            r={4}
                                            fill="#f97316"
                                          />,
                                        );
                                      }
                                      return icons.length > 0 ? (
                                        <g key={`flag-${i}`}>{icons}</g>
                                      ) : null;
                                    })}
                                  </g>
                                );
                              },
                            ]}
                          />
                        </div>
                      </div>
                    )}

                    {/* Insights Cards */}
                    {(() => {
                      const insights =
                        selectedTab === "Month"
                          ? monthlyWellnessInsights
                          : selectedTab === "Quarter"
                            ? quarterlyWellnessInsights
                            : weeklyWellnessInsights;
                      const hasInsights =
                        insights.bestCategories.length > 0 ||
                        insights.mostImproving ||
                        insights.mostDeclining;
                      if (!hasInsights) return null;
                      return (
                        <div className="mt-4 space-y-3 px-4 pb-32 sm:px-0 sm:pb-0">
                          {/* Best & Worst Categories */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {insights.bestCategories.length > 0 && (
                              <button
                                onClick={() =>
                                  setSelectedParameters([
                                    "score",
                                    ...insights.bestCategories.map(
                                      (c) => c.category,
                                    ),
                                  ])
                                }
                                className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-left hover:bg-emerald-500/15 transition-colors"
                              >
                                <p className="text-[10px] font-geist uppercase tracking-wide text-emerald-600 mb-1">
                                  Best Performing
                                </p>
                                {insights.bestCategories.map((c) => (
                                  <p
                                    key={c.category}
                                    className="text-sm font-geist font-medium text-base-content"
                                  >
                                    {c.label}{" "}
                                    <span className="text-base-content/40">
                                      ({Math.round(((c.average - 1) / 6) * 100)}
                                      %)
                                    </span>
                                  </p>
                                ))}
                              </button>
                            )}
                            {insights.worstCategories.length > 0 && (
                              <button
                                onClick={() =>
                                  setSelectedParameters([
                                    "score",
                                    ...insights.worstCategories.map(
                                      (c) => c.category,
                                    ),
                                  ])
                                }
                                className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-left hover:bg-red-500/15 transition-colors"
                              >
                                <p className="text-[10px] font-geist uppercase tracking-wide text-red-600 mb-1">
                                  Needs Attention
                                </p>
                                {insights.worstCategories.map((c) => (
                                  <p
                                    key={c.category}
                                    className="text-sm font-geist font-medium text-base-content"
                                  >
                                    {c.label}{" "}
                                    <span className="text-base-content/40">
                                      ({Math.round(((c.average - 1) / 6) * 100)}
                                      %)
                                    </span>
                                  </p>
                                ))}
                              </button>
                            )}
                          </div>

                          {/* Most Improving / Declining */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {insights.mostImproving && (
                              <button
                                onClick={() =>
                                  setSelectedParameters([
                                    "score",
                                    insights.mostImproving!.category,
                                  ])
                                }
                                className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-left hover:bg-emerald-500/15 transition-colors"
                              >
                                <p className="text-[10px] font-geist uppercase tracking-wide text-emerald-600 mb-1">
                                  Most Improving
                                </p>
                                <p className="text-sm font-geist font-medium text-base-content">
                                  {insights.mostImproving.label}
                                </p>
                                <p className="text-xs text-emerald-600 font-geist">
                                  ↑{" "}
                                  {Math.abs(
                                    insights.mostImproving.percentChange,
                                  ).toFixed(0)}
                                  %
                                </p>
                              </button>
                            )}
                            {insights.mostDeclining && (
                              <button
                                onClick={() =>
                                  setSelectedParameters([
                                    "score",
                                    insights.mostDeclining!.category,
                                  ])
                                }
                                className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-left hover:bg-red-500/15 transition-colors"
                              >
                                <p className="text-[10px] font-geist uppercase tracking-wide text-red-600 mb-1">
                                  Most Declining
                                </p>
                                <p className="text-sm font-geist font-medium text-base-content">
                                  {insights.mostDeclining.label}
                                </p>
                                <p className="text-xs text-red-600 font-geist">
                                  ↓{" "}
                                  {Math.abs(
                                    insights.mostDeclining.percentChange,
                                  ).toFixed(0)}
                                  %
                                </p>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Wellness Export Dialog */}
      <WellnessExportDialog
        isOpen={wellnessExportOpen}
        onClose={() => setWellnessExportOpen(false)}
        wellnessData={currentDayWellness}
        averageScore={averageScore}
        selectedTab={selectedTab}
      />

      {/* Custom Date Range Modal */}
      {showCustomDateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg max-w-md w-full">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-grotesk font-semibold text-base-content">
                  Custom Date Range
                </h3>
                <button
                  onClick={() => {
                    setShowCustomDateModal(false);
                    // Reset to Day tab if no custom data is loaded or no access
                    if (!hasCustomDateAccess || customRangeData.length === 0) {
                      setSelectedTab("Day");
                    }
                  }}
                  className="p-1 rounded-full hover:bg-base-200 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-base-content/60" />
                </button>
              </div>

              {!hasCustomDateAccess && (
                <ComingSoon featureKey="advancedGraphs" />
              )}

              <div className="space-y-4">
                {/* Date pickers - always visible but disabled if no access */}
                <div className="new-form-group w-full">
                  <input
                    id="custom-start-date"
                    type="date"
                    className={`form-element ${
                      hasCustomDateAccess
                        ? "cursor-pointer"
                        : "cursor-not-allowed opacity-60"
                    }`}
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    max={customEndDate || undefined}
                    disabled={!hasCustomDateAccess}
                    style={{
                      colorScheme: "light",
                      backgroundColor: "oklch(var(--b2))",
                      // iOS fix: Prevent date input from extending beyond container
                      maxWidth: "100%",
                      width: "100%",
                      fontSize: "16px", // Prevents iOS zoom on focus
                    }}
                    onFocus={(e) => {
                      if (hasCustomDateAccess) {
                        try {
                          e.target.showPicker?.();
                        } catch (error) {
                          // Fallback for browsers that don't support showPicker
                        }
                      }
                    }}
                  />
                  <label className="text-base-content/80">Start Date</label>
                </div>

                <div className="new-form-group w-full">
                  <input
                    id="custom-end-date"
                    type="date"
                    className={`form-element ${
                      hasCustomDateAccess
                        ? "cursor-pointer"
                        : "cursor-not-allowed opacity-60"
                    }`}
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    min={customStartDate || undefined}
                    disabled={!hasCustomDateAccess}
                    style={{
                      colorScheme: "light",
                      backgroundColor: "oklch(var(--b2))",
                      // iOS fix: Prevent date input from extending beyond container
                      maxWidth: "100%",
                      width: "100%",
                      fontSize: "16px", // Prevents iOS zoom on focus
                    }}
                    onFocus={(e) => {
                      if (hasCustomDateAccess) {
                        try {
                          e.target.showPicker?.();
                        } catch (error) {
                          // Fallback for browsers that don't support showPicker
                        }
                      }
                    }}
                  />
                  <label className="text-base-content/80">End Date</label>
                </div>

                {/* Load Data Button */}
                {hasCustomDateAccess && (
                  <button
                    onClick={async () => {
                      if (customStartDate && customEndDate) {
                        await fetchCustomRangeData(
                          customStartDate,
                          customEndDate,
                        );
                        setSelectedTab("Custom");
                        setShowCustomDateModal(false);
                      }
                    }}
                    disabled={
                      !customStartDate || !customEndDate || customRangeLoading
                    }
                    className={`w-full py-3 px-4 rounded-xl font-grotesk font-medium transition-colors ${
                      !customStartDate || !customEndDate || customRangeLoading
                        ? "bg-base-300 text-base-content/60 cursor-not-allowed"
                        : "bg-primary text-white hover:bg-primary/90"
                    }`}
                  >
                    {customRangeLoading ? "Loading..." : "Load Data"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wellness Log Form */}
      <AddWellnessLogForm
        open={showWellnessForm}
        onClose={() => setShowWellnessForm(false)}
        onSubmit={async (wellnessLog) => {
          if (!currentUser?.uid) return;
          const todayDateID = generateDateID(new Date());
          await addWellnessLogWithPeakScore(
            currentUser.uid,
            wellnessLog,
            todayDateID,
          );
          setShowWellnessForm(false);
          // Reload the page data
          window.location.reload();
        }}
        dateID={generateDateID(new Date())}
        editMode={false}
        trainingWarning={true}
      />
    </div>
  );
};

export default WellnessInsights;
